import { createSelector } from 'reselect';
import { AppState } from '../..';
import {
  getAllActiveFollowsForCurrentMemberWithSignal,
  getAllFollowsForCurrentMemberWithSignal,
} from '../follows';
import { Signal, SignalShare } from './models';

const sortShares = (shares: SignalShare[]) =>
  shares.sort((a, b) => (a.created_at < b.created_at ? 1 : -1));

export const sortSignalsByTimestamp = (a: Signal, b: Signal): 1 | -1 =>
  a.timestamp < b.timestamp ? 1 : -1;

export const sortSignalsBySubscribedThen =
  (subComparator: (sa: Signal, sb: Signal) => 1 | -1 | 0) =>
  (a: Signal, b: Signal): 1 | -1 | 0 => {
    if (a.subscribed === b.subscribed) {
      return subComparator(a, b);
    }
    if (a.subscribed) {
      return -1;
    }

    return 1;
  };

export const sortSignalsByDistance = (a: Signal, b: Signal): 1 | -1 =>
  a.distance_from_member > b.distance_from_member ? 1 : -1;

export const selectSignals = (state: AppState) => state.signal.signals;

export const selectAllSignals = (state: AppState): Signal[] =>
  Object.values(state.signal.signals);

export const selectPublicSignalsOnTheWater = createSelector(
  selectSignals,
  (signals) =>
    Object.values(signals)
      .filter((signal) => signal.online)
      .filter((signal) => signal.on_the_water),
);

export const selectSignalSharesLoading = createSelector(
  (state: AppState) => state.signal.signalSharesLoading,
  (loading) => loading,
);

export const getSignalForCurrentMember = createSelector(
  (state: AppState) => state.backendService?.currentMember?.id,
  selectSignals,
  (memberId, signals) =>
    Object.values(signals).find((signal) => signal.memberid === `${memberId}`),
);

export const getOnTheWaterFromState = createSelector(
  getSignalForCurrentMember,
  (signal) => signal?.on_the_water,
);

export const getSignal = createSelector(
  (state: AppState, id: string) => id,
  (state: AppState) => state.signal,
  (id, signal) => signal.signals[id],
);

export const getSelectedSignal = createSelector(
  (state: AppState) => state.signal.selectedSignal || '',
  (state: AppState) => state.signal,
  (id, signal) => signal.signals[id],
);

export const getSignalSharedWithCurrentMember = createSelector(
  (state: AppState) => state.backendService.currentMember.id,
  (state: AppState) => state.signal.signalShares,
  (currentMemberId, shares) =>
    sortShares(
      Object.values(shares).filter(
        (share) =>
          share.sharedWith.id === `${currentMemberId}` &&
          !!share.sharedBy.positionId,
      ),
    ),
);

export const getSignalSharedByCurrentMember = createSelector(
  (state: AppState) => state.backendService.currentMember.id,
  (state: AppState) => state.signal.signalShares,
  (currentMemberId, shares) =>
    sortShares(
      Object.values(shares)
        .filter((share) => share.sharedBy.id === `${currentMemberId}`)
        .sort((a, b) => (a.created_at < b.created_at ? 1 : -1)),
    ),
);

export const getAllSignalsSharedWithCurrentMember = createSelector(
  selectSignals,
  getSignalSharedWithCurrentMember,
  (signals, shares) =>
    shares
      .filter((share) => share?.sharedBy?.positionId)
      .map((share) => signals[share.sharedBy.positionId as string])
      .filter((signal) => !!signal)
      .sort(sortSignalsBySubscribedThen(sortSignalsByTimestamp)),
);

export const getAllSignalsFollowedByTheCurrentMember = createSelector(
  getAllActiveFollowsForCurrentMemberWithSignal,
  (follows) => follows.map((follow) => follow.followee?.signal),
);

export const selectAllSignalsNotSharedOrFollowedByCurrentMember =
  createSelector(
    (state: AppState) => state.backendService.currentMember.id,
    selectPublicSignalsOnTheWater,
    getSignalSharedByCurrentMember,
    getAllFollowsForCurrentMemberWithSignal,
    (memberId, signals, shares, follows) => {
      const sharedByIds = new Set(shares.map((share) => share.sharedBy.id));
      return (
        Object.values(signals)
          .filter(
            (signal) =>
              signal.memberid !== `${memberId}` &&
              !sharedByIds.has(signal.memberid),
          )
          // Filter out signals from members that are followed
          .filter(
            (signal) =>
              !follows.find((follow) => follow.followee.id === signal.memberid),
          )
          .sort(sortSignalsBySubscribedThen(sortSignalsByDistance))
      );
    },
  );
